import { useState } from "react";

const ELEMENTS = ["dark", "dragon", "electric", "fire", "grass", "ground", "ice", "neutral", "water"];

// Aliases: some data sources (palpedia, community scrapes) label the
// "Neutral" element as "Normal", and palworld.gg calls "Ground" →
// "Earth". Map them to the canonical key so the chip + asset lookup
// always resolve.
const ELEMENT_ALIASES = {
  normal: "neutral",
  earth: "ground",
};

const ELEMENT_COLORS = {
  fire: "#FF6B35",
  water: "#4FC3F7",
  electric: "#FFD600",
  grass: "#66BB6A",
  ice: "#81D4FA",
  ground: "#8D6E63",
  dragon: "#9D4CDD",
  dark: "#5A4A6F",
  neutral: "#B0BEC5",
};

// Discord application-emoji IDs for our `ghoul<element>` set
// (uploaded to the Chillet's Palworld Bot application). We render
// these as `cdn.discordapp.com/emojis/{id}.png` so the website uses
// the *exact same* art the in-game Discord embeds do. Source of
// truth: /app/backend/data/element_emoji_ids.json — keep in sync
// when adding new elements.
const ELEMENT_EMOJI_IDS = {
  fire:     "1485468512897073202",
  water:    "1485468513907773450",
  electric: "1485468514541240540",
  grass:    "1485468515128574074",
  ground:   "1485468515724034215",
  ice:      "1485468516986650675",
  dark:     "1485468517653413908",
  dragon:   "1485468518597263450",
  neutral:  "1485468519771541695",
};

const ASSETS = `${process.env.REACT_APP_BACKEND_URL}/api/assets`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** Single-request pal art URL. The backend does all the name
 *  normalisation and falls back to the in-game paldeck icon. */
export const palCardUrl = (name) =>
  `${API_BASE}/api/pal-card/${encodeURIComponent(name || "")}`;

/** Build the Discord CDN URL for an element. Discord serves app
 *  emoji at /emojis/{id}.png — same URL the bot embeds use. */
function elementImageUrl(lower, size) {
  const eid = ELEMENT_EMOJI_IDS[lower];
  if (eid) {
    // `size` query is honoured by Discord's CDN (it rescales server-
    // side). 64 covers anything up to a small badge; bump to 128 for
    // big detail callouts.
    const px = size <= 32 ? 64 : 128;
    return `https://cdn.discordapp.com/emojis/${eid}.png?size=${px}&quality=lossless`;
  }
  // Fallback to the bundled webp (legacy / offline-resilient path)
  return `${ASSETS}/elements/${lower}.webp`;
}

/**
 * Tiny rounded chip showing a Palworld element with its icon.
 * Pass `size` for the icon px (default 16). Element name is case-insensitive.
 */
export function ElementChip({ element, size = 16, showLabel = true }) {
  let lower = (element || "").toLowerCase();
  if (ELEMENT_ALIASES[lower]) lower = ELEMENT_ALIASES[lower];
  if (!ELEMENTS.includes(lower)) return null;
  const color = ELEMENT_COLORS[lower];
  return (
    <span
      data-testid={`element-chip-${lower}`}
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider"
      style={{
        background: `${color}22`,
        border: `1px solid ${color}66`,
        color: color,
      }}
    >
      <img
        src={elementImageUrl(lower, size)}
        alt={lower}
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: "contain" }}
        loading="lazy"
        draggable={false}
        // If Discord's CDN is unreachable (sanctions / outage), fall
        // back to the bundled webp so the chip never goes blank.
        onError={(e) => {
          if (!e.currentTarget.dataset.fallback) {
            e.currentTarget.dataset.fallback = "1";
            e.currentTarget.src = `${ASSETS}/elements/${lower}.webp`;
          }
        }}
      />
      {showLabel && <span>{lower}</span>}
    </span>
  );
}

/**
 * Pal portrait. The backend resolves the art now (`/api/pal-card/<name>`):
 * it knows the asset directory, strips boss/alpha/variant naming itself and
 * falls back to the in-game paldeck icon, so ONE request replaces the old
 * 6-normalisations x 4-extensions chain that fired up to 24 404s per unknown
 * pal. `wikiImage` stays as a last resort for pals we have no local art for.
 */
const _resolvedPortraitCache = new Map();  // name → working src URL

export function PalPortrait({ name, wikiImage, className = "", size = "w-24 h-24" }) {
  const sources = [
    name ? `${API_BASE}/api/pal-card/${encodeURIComponent(name)}` : "",
    wikiImage || "",
  ].filter(Boolean);

  const cachedSrc = _resolvedPortraitCache.get(name);
  const startIdx = cachedSrc ? Math.max(0, sources.indexOf(cachedSrc)) : 0;

  const [idx, setIdx] = useState(startIdx);
  const [trackedName, setTrackedName] = useState(name);
  if (trackedName !== name) {
    setTrackedName(name);
    setIdx(0);
  }

  if (idx >= sources.length) {
    return <div className={`${size} ${className} bg-black/40 rounded`} data-testid={`pal-portrait-${name}-empty`} />;
  }

  return (
    <img
      data-testid={`pal-portrait-${name}`}
      src={sources[idx]}
      alt={name}
      onLoad={() => _resolvedPortraitCache.set(name, sources[idx])}
      onError={() => setIdx((i) => i + 1)}
      className={`${size} ${className} object-cover rounded bg-black/40`}
      loading="lazy"
      draggable={false}
    />
  );
}
